<template>
  <div class="user-right-page">
    <el-card shadow="hover" class="user-list-card">
      <div class="header">
        <div class="header-left">
          <span class="header-title">用户管理</span>
        </div>
        <div class="header-right">
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="openDialog()">新增用户</el-button>
        </div>
      </div>

      <!-- 搜索栏 -->
      <div class="search-bar">
        <el-form :inline="true" :model="searchForm" size="mini">
          <el-form-item label="用户名称">
            <el-input v-model="searchForm.userName" placeholder="请输入用户名称" clearable />
          </el-form-item>
          <el-form-item label="登录账号">
            <el-input v-model="searchForm.loginAccount" placeholder="请输入登录账号" clearable />
          </el-form-item>
          <el-form-item label="角色类型" v-if="isAdmin">
            <el-select v-model="searchForm.roleType" placeholder="请选择" clearable>
              <el-option
                v-for="(item, index) in roleTypeOptions"
                :key="`role-type-${item.dictValue}-${index}`"
                :label="item.dictLabel"
                :value="parseInt(item.dictValue)"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="账户状态">
            <el-select v-model="searchForm.accountStatus" placeholder="请选择" clearable>
              <el-option
                v-for="(item, index) in accountStatusOptions"
                :key="`account-status-${item.dictValue}-${index}`"
                :label="item.dictLabel"
                :value="parseInt(item.dictValue)"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="所属单位" v-if="isAdmin">
            <el-select v-model="searchForm.tenantId" placeholder="请选择" clearable filterable>
              <el-option
                v-for="(item, index) in tenantOptions"
                :key="`tenant-search-${item.tenantId}-${index}`"
                :label="item.tenantName"
                :value="item.tenantId"
              />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- 用户列表 -->
      <el-table :data="userList" border style="width: 100%" v-loading="loading">
        <el-table-column prop="userName" label="用户名称" align="center" />
        <el-table-column prop="loginAccount" label="登录账号" align="center" />
        <el-table-column prop="mobilePhone" label="手机号码" align="center" />
        <el-table-column prop="email" label="邮箱" align="center" />
        <el-table-column prop="tenantName" label="所属单位" align="center" />
        <el-table-column prop="roleType" label="角色类型" align="center">
          <template slot-scope="scope">
            {{ getDictLabel(roleTypeOptions, scope.row.roleType) }}
          </template>
        </el-table-column>
        <el-table-column prop="accountStatus" label="账户状态" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.accountStatus === 1 ? 'success' : 'info'">
              {{ getDictLabel(accountStatusOptions, scope.row.accountStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" align="center">
          <template slot-scope="scope">
            <el-button size="mini" type="text" icon="el-icon-edit" @click="openDialog(scope.row)">编辑</el-button>
            <el-button 
              size="mini" 
              type="text" 
              icon="el-icon-delete" 
              class="delete-btn"
              @click="handleDelete(scope.row)"
            >删除</el-button>
            <el-button 
              size="mini" 
              type="text" 
              icon="el-icon-key"
              @click="handleResetPassword(scope.row)"
            >重置密码</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="pagination.current"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pagination.size"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total"
        />
      </div>
    </el-card>

    <!-- 用户表单弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="500px" @close="resetForm">
      <el-form :model="userForm" :rules="userRules" ref="userFormRef" label-width="100px">
        <el-form-item label="用户名称" prop="userName">
          <el-input v-model="userForm.userName" maxlength="100" show-word-limit />
        </el-form-item>
        <el-form-item label="登录账号" prop="loginAccount">
          <el-input v-model="userForm.loginAccount" maxlength="100" show-word-limit :disabled="!!userForm.userId" />
        </el-form-item>
        <el-form-item label="登录密码" prop="loginPassword" v-if="!userForm.userId">
          <el-input v-model="userForm.loginPassword" type="password" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="手机号码" prop="mobilePhone">
          <el-input v-model="userForm.mobilePhone" maxlength="20" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="userForm.email" maxlength="100" />
        </el-form-item>
        <el-form-item label="所属单位" prop="tenantId" v-if="isAdmin">
          <el-select v-model="userForm.tenantId" placeholder="请选择" filterable>
            <el-option
              v-for="(item, index) in tenantOptions"
              :key="`tenant-form-${item.tenantId}-${index}`"
              :label="item.tenantName"
              :value="item.tenantId"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="角色类型" prop="roleType" v-if="isAdmin">
          <el-select v-model="userForm.roleType" placeholder="请选择">
            <el-option
              v-for="(item, index) in roleTypeOptions"
              :key="`role-form-${item.dictValue}-${index}`"
              :label="item.dictLabel"
              :value="parseInt(item.dictValue)"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="账户状态" prop="accountStatus">
          <el-select v-model="userForm.accountStatus" placeholder="请选择">
            <el-option
              v-for="(item, index) in accountStatusOptions"
              :key="`account-form-${item.dictValue}-${index}`"
              :label="item.dictLabel"
              :value="parseInt(item.dictValue)"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getDictData } from '@/api/dict'
import request from '@/utils/request'

export default {
  name: 'UserRight',
  data() {
    // 手机号码验证
    const validateMobile = (rule, value, callback) => {
      if (value && !/^1[3-9]\d{9}$/.test(value)) {
        callback(new Error('请输入正确的手机号码'))
      } else {
        callback()
      }
    }
    // 邮箱验证
    const validateEmail = (rule, value, callback) => {
      if (value && !/^[a-zA-Z0-9_-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/.test(value)) {
        callback(new Error('请输入正确的邮箱地址'))
      } else {
        callback()
      }
    }

    return {
      // 搜索表单
      searchForm: {
        userName: '',
        loginAccount: '',
        roleType: undefined,
        accountStatus: undefined,
        tenantId: ''
      },
      // 用户列表
      userList: [],
      loading: false,
      // 分页
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      // 弹窗
      dialogVisible: false,
      dialogTitle: '',
      // 用户表单
      userForm: {
        userId: '',
        userName: '',
        loginAccount: '',
        loginPassword: '',
        mobilePhone: '',
        email: '',
        tenantId: '',
        roleType: undefined,
        accountStatus: 1
      },
      // 表单校验规则
      userRules: {
        userName: [
          { required: true, message: '请输入用户名称', trigger: 'blur' },
          { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        loginAccount: [
          { required: true, message: '请输入登录账号', trigger: 'blur' },
          { min: 4, max: 100, message: '长度在 4 到 100 个字符', trigger: 'blur' }
        ],
        loginPassword: [
          { required: true, message: '请输入登录密码', trigger: 'blur' },
          { min: 6, max: 20, message: '长度在 6 到 20 个字符', trigger: 'blur' }
        ],
        mobilePhone: [{ validator: validateMobile, trigger: 'blur' }],
        email: [{ validator: validateEmail, trigger: 'blur' }],
        roleType: [{ required: true, message: '请选择角色类型', trigger: 'change' }],
        accountStatus: [{ required: true, message: '请选择账户状态', trigger: 'change' }],
        tenantId: [{ required: true, message: '请选择所属单位', trigger: 'change' }]
      },
      // 字典数据
      roleTypeOptions: [],
      accountStatusOptions: [],
      // 租户选项
      tenantOptions: [],
      // 当前用户信息
      currentUser: null
    }
  },
  computed: {
    // 是否是超级管理员
    isAdmin() {
      return this.currentUser && this.currentUser.roleType === 1
    }
  },
  async created() {
    this.getCurrentUser()
    this.loadDictData()
    await this.loadTenantOptions()
    this.fetchUserList()
  },
  methods: {
    // 获取当前用户信息
    async getCurrentUser() {
      try {
        const res = await request({
          url: '/auth/info',
          method: 'get'
        })
        this.currentUser = res.data
        
        // 如果不是超级管理员，设置默认租户ID
        if (!this.isAdmin) {
          this.userForm.tenantId = this.currentUser.tenantId
          this.userForm.roleType = 3
        }
      } catch (error) {
        console.error('获取当前用户信息失败:', error)
      }
    },
    // 加载字典数据
    async loadDictData() {
      try {
        const [roleTypeRes, accountStatusRes] = await Promise.all([
          getDictData('role_type'),
          getDictData('account_status')
        ])
        this.roleTypeOptions = roleTypeRes.data || []
        this.accountStatusOptions = accountStatusRes.data || []
      } catch (error) {
        console.error('加载字典数据失败:', error)
      }
    },
    // 加载租户选项
    async loadTenantOptions() {
      try {
        const res = await request({
          url: '/tenant/list',
          method: 'get',
          params: {
            pageNum: 1,
            pageSize: 1000
          }
        })
        this.tenantOptions = res.data.records || []
      } catch (error) {
        console.error('加载租户选项失败:', error)
      }
    },
    // 获取字典标签
    getDictLabel(options, value) {
      const option = options.find(item => parseInt(item.dictValue) === value)
      return option ? option.dictLabel : value
    },
    // 查询用户列表
    async fetchUserList() {
      this.loading = true
      try {
        const params = {
          ...this.searchForm,
          current: this.pagination.current,
          size: this.pagination.size
        }
        const res = await request({
          url: '/user/list',
          method: 'get',
          params
        })
        
        // 处理用户列表数据，添加租户名称
        this.userList = (res.data.records || []).map(user => {
          const tenant = this.tenantOptions.find(t => t.tenantId === user.tenantId)
          return {
            ...user,
            tenantName: tenant ? tenant.tenantName : user.tenantId
          }
        })
        this.pagination.total = res.data.total || 0
      } catch (error) {
        console.error('查询用户列表失败:', error)
      } finally {
        this.loading = false
      }
    },
    // 搜索
    handleSearch() {
      this.pagination.current = 1
      this.fetchUserList()
    },
    // 重置搜索
    resetSearch() {
      this.searchForm = {
        userName: '',
        loginAccount: '',
        roleType: undefined,
        accountStatus: undefined,
        tenantId: ''
      }
      this.handleSearch()
    },
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.size = val
      this.fetchUserList()
    },
    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.current = val
      this.fetchUserList()
    },
    // 打开弹窗
    openDialog(row) {
      this.dialogTitle = row ? '编辑用户' : '新增用户'
      if (row) {
        this.userForm = {
          ...row,
          roleType: parseInt(row.roleType),
          accountStatus: parseInt(row.accountStatus)
        }
      } else {
        this.userForm = {
          userId: '',
          userName: '',
          loginAccount: '',
          loginPassword: '',
          mobilePhone: '',
          email: '',
          tenantId: this.isAdmin ? '' : this.currentUser.tenantId,
          roleType: this.isAdmin ? undefined : 3,
          accountStatus: 1
        }
      }
      this.dialogVisible = true
    },
    // 重置表单
    resetForm() {
      if (this.$refs.userFormRef) {
        this.$refs.userFormRef.resetFields()
      }
    },
    // 提交表单
    async handleSubmit() {
      try {
        await this.$refs.userFormRef.validate()
        const isEdit = !!this.userForm.userId
        await request({
          url: isEdit ? '/user/update' : '/user/add',
          method: isEdit ? 'put' : 'post',
          data: this.userForm
        })
        this.$message.success('保存成功')
        this.dialogVisible = false
        this.fetchUserList()
      } catch (error) {
        if (error === false) return // 表单验证失败
        console.error('保存用户失败:', error)
      }
    },
    // 删除用户
    async handleDelete(row) {
      try {
        await this.$confirm('确认删除该用户吗？', '提示', {
          type: 'warning'
        })
        await request({
          url: `/user/delete/${row.userId}`,
          method: 'delete'
        })
        this.$message.success('删除成功')
        this.fetchUserList()
      } catch (error) {
        if (error === 'cancel') return
        console.error('删除用户失败:', error)
      }
    },
    // 重置用户密码
    async handleResetPassword(row) {
      try {
        await this.$confirm('确认要重置该用户的密码吗？', '提示', {
          type: 'warning',
          confirmButtonText: '确定',
          cancelButtonText: '取消'
        })
        
        const res = await request({
          url: `/user/reset-password/${row.userId}`,
          method: 'post'
        })
        
        // 显示新密码
        await this.$alert(`新密码: ${res.data}`, '密码重置成功', {
          confirmButtonText: '确定',
          callback: () => {
            // 可以在这里添加复制密码到剪贴板的功能
          }
        })
      } catch (error) {
        if (error === 'cancel') return
        console.error('重置密码失败:', error)
      }
    }
  }
}
</script>

<style scoped>
.user-right-page {
  padding: 32px 24px 24px 24px;
  background: #f4f6fa;
}

.user-list-card {
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.04);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.search-bar {
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  color: #f78989;
}

/* 表格样式优化 */
.el-table th {
  background-color: #fafbfc;
  color: #666;
  font-weight: 500;
}

/* 弹窗表单样式 */
.el-dialog__body {
  padding: 20px 40px;
}
</style> 